import { PhoneOutlined } from "@ant-design/icons";
import { Avatar, Divider, Flex } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { UserShortWithWork } from "tm-shared/types";

import {
    DashboardTabs,
    Salary,
    SelectedDateSalaryState,
} from "@/features/Dashboard";
import { PageBlock } from "@/shared/pageComposition/BlockList";
import { BlockLoader, PrimaryText, SecondaryText } from "@/shared/ui";

import { useMemberInfoData } from "./hooks/useMemberInfoData";
import { MemberInfoCapabilities } from "./MemberInfo.policy";
import { StyledMemberBadge, StyledMemberLogo, StyledPhoneLink } from "./Styled";

export type MemberInfoModel = {
    capabilities: MemberInfoCapabilities;
    memberData: ReturnType<typeof useMemberInfoData>;
    salary: {
        selectedDateSalary: SelectedDateSalaryState;
        onSalaryDataChange: (state: SelectedDateSalaryState) => void;
    };
};

const MemberHeaderBlock = ({ member }: { member?: UserShortWithWork }) => {
    const userStatusText = useMemo(() => {
        return member?.isWorking
            ? `Почав о ${dayjs(member.startedAt).format("H:mm")}`
            : "Не на роботі";
    }, [member]);

    return (
        <Flex gap={16}>
            <StyledMemberBadge dot $isWorking={!!member?.isWorking}>
                <Avatar size={42} icon={<StyledMemberLogo />} />
            </StyledMemberBadge>

            <Flex vertical align="flex-start" gap={8}>
                <PrimaryText>{member?.userInfo?.fullName}</PrimaryText>
                <SecondaryText>{userStatusText}</SecondaryText>

                <StyledPhoneLink href={`tel:${member?.userInfo?.phone}`}>
                    <SecondaryText>{member?.userInfo?.phone}</SecondaryText>

                    <SecondaryText>
                        <PhoneOutlined />
                    </SecondaryText>
                </StyledPhoneLink>
            </Flex>
        </Flex>
    );
};

const SalaryBlock = ({
    dashboardLoaded,
    selectedDateSalary,
}: {
    dashboardLoaded: boolean;
    selectedDateSalary: SelectedDateSalaryState;
}) => {
    if (!dashboardLoaded) {
        return (
            <Flex vertical align="center">
                <BlockLoader />
            </Flex>
        );
    }

    return <Salary source="work" selectedDateSalary={selectedDateSalary} />;
};

const DashboardBlock = ({
    dashboardLoaded,
    employeeId,
    routeId,
    onSalaryDataChange,
}: {
    dashboardLoaded: boolean;
    employeeId?: string;
    routeId?: string;
    onSalaryDataChange: (state: SelectedDateSalaryState) => void;
}) => {
    if (!employeeId || !routeId || !dashboardLoaded) return null;

    return (
        <DashboardTabs
            employeeId={employeeId}
            routeId={routeId}
            onSalaryDataChange={onSalaryDataChange}
        />
    );
};

export const getMemberInfoBlocks = (
    model: MemberInfoModel,
): PageBlock<MemberInfoModel>[] => [
    {
        key: "member-header",
        order: 10,
        isVisible: (model) => model.capabilities.canSeeMemberHeader,
        render: () => <MemberHeaderBlock member={model.memberData.member} />,
    },
    {
        key: "divider",
        order: 20,
        isVisible: () => true,
        render: () => <Divider style={{ margin: "16px 0" }} />,
    },
    {
        key: "salary",
        order: 30,
        isVisible: (model) => model.capabilities.canSeeSalary,
        render: () => (
            <SalaryBlock
                dashboardLoaded={model.memberData.dashboardLoaded}
                selectedDateSalary={model.salary.selectedDateSalary}
            />
        ),
    },
    {
        key: "dashboard",
        order: 40,
        isVisible: (model) => model.capabilities.canSeeDashboardTabs,
        render: () => (
            <DashboardBlock
                dashboardLoaded={model.memberData.dashboardLoaded}
                employeeId={model.memberData.employeeId}
                routeId={model.memberData.routeId}
                onSalaryDataChange={model.salary.onSalaryDataChange}
            />
        ),
    },
];
